import { useCallback } from 'react';

import { shouldReloadForNewVersion } from './versionPolling';

type To = string | { pathname?: string; search?: string; hash?: string };

function getTargetPathname(to: To, currentPathname: string): string | undefined {
  if (typeof to !== 'string') {
    return to.pathname;
  }

  // Query/hash-only string destinations do not change pathname.
  if (to.startsWith('?') || to.startsWith('#')) {
    return currentPathname;
  }

  try {
    return new URL(to, window.location.href).pathname;
  } catch {
    return undefined;
  }
}

/**
 * Wraps a navigate function to force a full page reload when a new frontend
 * version has been deployed. Only pathname changes are intercepted — query/hash-only
 * changes pass through to normal SPA navigation.
 *
 * Accepts `navigate` and `pathname` as arguments so the package doesn't need
 * a dependency on react-router. All extra arguments (e.g. react-router's
 * `NavigateOptions` carrying `state`) are forwarded to the underlying navigate
 * call so callers like react-aria's RouterProvider can pass `routerOptions` through.
 */
export function useVersionAwareNavigate<TArgs extends unknown[]>(
  navigate: (to: To, ...args: TArgs) => void,
  pathname: string,
) {
  return useCallback(
    (to: To, ...rest: TArgs) => {
      const targetPath = getTargetPathname(to, pathname);
      if (targetPath && targetPath !== pathname && shouldReloadForNewVersion()) {
        const href = typeof to === 'string' ? to : (to.pathname ?? '/') + (to.search ?? '') + (to.hash ?? '');
        window.location.assign(href);
        return;
      }
      navigate(to, ...rest);
    },
    [navigate, pathname],
  );
}
